import type { WebDropinAdditionalFields } from '@/lib/types'

/**
 * Checkout field mode driven by the company's `webDropinAdditionalFields`
 * settings (JET-2000 / JET-2002). See specs/app/index.md::Decisions for the
 * composition rule against the pre-existing knobs (`?billingInfo`,
 * `collectPayerInfo`, `VITE_WEB_DROP_IN_SHOW_NAME`).
 * - 'default' — card number, expiry, security code only (pre-JET-2000 shape).
 * - 'zip' — additionally cardholder name + postal code.
 * - 'full' — additionally cardholder name + the full billing address.
 */
export type CheckoutFieldMode = 'default' | 'zip' | 'full'

/**
 * Derives the checkout field mode from the company's web-drop-in
 * additional-fields settings. Mirrors the derivation shipped on the JET-1986
 * web-app branch: `fullBillingAddress` wins over `postalCode`. `null` input
 * (fetch failure, missing `transactionId`, or BE field not yet deployed)
 * fails open to `'default'` — never blocks the checkout.
 */
export function resolveCheckoutFieldMode(fields: WebDropinAdditionalFields | null): CheckoutFieldMode {
  const fullBillingAddress = fields?.fullBillingAddress ?? false
  const postalCodeOnly = (fields?.postalCode ?? false) && !fullBillingAddress

  if (fullBillingAddress) return 'full'
  if (postalCodeOnly) return 'zip'
  return 'default'
}

/**
 * Adyen card-config fragment implied by a checkout field mode. Callers spread
 * this into the existing `cardConfig` object built in use-adyen-checkout.ts,
 * alongside the pre-existing knobs.
 */
export interface CheckoutFieldModeCardConfig {
  billingAddressMode?: 'partial' | 'full'
  requiresHolderName: boolean
}

/**
 * Maps a checkout field mode to its Adyen card-config fragment. `'default'`
 * omits `billingAddressMode` entirely (no key at all, not an explicit
 * `undefined`) so downstream consumers using the `...(condition && {...})`
 * spread idiom compose correctly without overriding a sibling knob's value.
 */
export function checkoutFieldModeToCardConfig(mode: CheckoutFieldMode): CheckoutFieldModeCardConfig {
  switch (mode) {
    case 'full':
      return { billingAddressMode: 'full', requiresHolderName: true }
    case 'zip':
      return { billingAddressMode: 'partial', requiresHolderName: true }
    default:
      return { requiresHolderName: false }
  }
}
